import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertCircle,
  ArrowRight,
  Bug,
  CalendarSearch,
  CalendarX2,
  CheckSquare,
  ClipboardList,
  Hourglass,
  Inbox,
  Wrench,
  IdCard,
  type LucideIcon,
} from 'lucide-react'
import { useGetTodayPlatform } from '../../hooks/backend/today'
import { Spinner } from '../../lib/shadcn/spinner'
import { Button } from '../../lib/shadcn/button'
import { Chip } from '../interviews/Chip'
import { ageText, platformTotal, type PlatformGroup } from '../../utils/today'

// Everything here arrives decided. Which kinds exist, what counts as due, the
// group labels, the counts and the order all come from today_platform_v1
// (migration 0271). This component lays them out and nothing more: CLAUDE.md
// rule 4.

type PlatformData = { groups: PlatformGroup[]; linked: boolean }

const ICONS: Record<string, LucideIcon> = {
  interview: ClipboardList,
  // 2870: a booking that is on someone's calendar but reached no applicant.
  calendly_unmatched: CalendarSearch,
  task: CheckSquare,
  service: Wrench,
  platform: Bug,
  stalled: Hourglass,
  compliance: CalendarX2,
  prdp_followup: IdCard,
}

// Enough to see the oldest few without a thirty-row wall on a phone.
const COLLAPSED = 5

function GroupCard({ group }: { group: PlatformGroup }) {
  const [expanded, setExpanded] = useState(false)
  const Icon = ICONS[group.kind] ?? Inbox
  const shown = expanded ? group.items : group.items.slice(0, COLLAPSED)
  const hidden = group.items.length - shown.length

  return (
    <section className="rounded-lg border border-border bg-card shadow-retool-sm" data-kind={group.kind}>
      <header className="flex items-center gap-2 border-b border-border px-4 py-3">
        <Icon className="h-4 w-4 shrink-0 text-[hsl(var(--brand))]" />
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{group.label}</h3>
        <Chip tone="brand">
          <span className="tabular-nums" data-testid="group-count">
            {group.count}
          </span>
        </Chip>
      </header>
      <ul className="divide-y divide-border">
        {shown.map((item) => {
          const age = ageText(item)
          return (
            <li key={item.item_key}>
              <Link
                to={item.link}
                className="group flex items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/50"
              >
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="break-words text-sm font-medium text-foreground">{item.title}</p>
                  {item.detail && <p className="break-words text-xs text-muted-foreground">{item.detail}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
                  {age && <span className="hidden text-xs tabular-nums text-muted-foreground sm:inline">{age}</span>}
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </div>
              </Link>
              {age && <p className="-mt-2 px-4 pb-3 text-xs tabular-nums text-muted-foreground sm:hidden">{age}</p>}
            </li>
          )
        })}
      </ul>
      {group.items.length > COLLAPSED && (
        <div className="border-t border-border px-4 py-2">
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Show the oldest five' : `Show all ${group.items.length}`}
            {!expanded && hidden > 0 ? ` (${hidden} more)` : ''}
          </Button>
        </div>
      )}
    </section>
  )
}

export function PlatformToday({ onTotal }: { onTotal?: (n: number | null) => void }) {
  const { data, loading, error, trigger } = useGetTodayPlatform<PlatformData>()

  const load = useCallback(() => {
    trigger({}, { skipCache: true }).catch(() => undefined)
  }, [trigger])

  useEffect(() => {
    load()
  }, [load])

  const groups = data?.groups ?? []

  useEffect(() => {
    onTotal?.(data ? platformTotal(data.groups ?? []) : null)
  }, [data, onTotal])

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error}</span>
      </div>
    )
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Loading everything due today…
      </div>
    )
  }

  if (data && !data.linked) {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
        Your login is not linked to a person in cockpit access, so the Platform view cannot be built for you. Ask an
        admin to link it.
      </div>
    )
  }

  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border p-12 text-center text-muted-foreground">
        <Inbox className="h-8 w-8 opacity-40" />
        <p className="text-sm font-medium text-foreground">Nothing across the cockpit is due today</p>
        <p className="text-sm">Interviews, tasks, services, requests, stalled applications and expiries are all clear.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <GroupCard key={g.kind} group={g} />
      ))}
    </div>
  )
}
